import { createTheme, useMediaQuery } from "@mui/material";
import React from "react";
import { useSelector } from "react-redux";
import COLORS from "../colors/colors";

const useFistoTheme = () => {
  const colorScheme = useSelector((state) => state.theme);
  const prefersDarkMode = useMediaQuery("(prefers-color-scheme: dark)");

  React.useEffect(() => {
    document.body.classList.add(colorScheme);
    return () => {
      document.body.classList.remove(colorScheme);
    };
  }, [colorScheme]);

  const theme = React.useMemo(
    () =>
      createTheme({
        typography: {
          fontFamily: "'Open Sans', sans-serif",
          fontSize: 12,
          heading: {
            margin: 0,
            fontSize: "1.35em",
            fontWeight: 700,
            color:
              colorScheme === "dark" || (colorScheme === "system" && prefersDarkMode)
                ? COLORS.WHITE
                : COLORS.PRIMARY_COLOR,
          },
          permission: {
            margin: 0,
            fontSize: "1rem",
            fontWeight: 700,
          },
          sidebar: {
            marginLeft: 20,
            fontWeight: 500,
          },
        },
        components: {
          MuiInputLabel: {
            styleOverrides: {
              root: {
                fontSize: "0.75em",
                transform: "translate(14px, 10px) scale(1)",
              },
              shrink: {
                transform: "translate(18px, -6px) scale(0.85)",
              },
            },
          },
          MuiAccordion: {
            styleOverrides: {
              root: {
                color: "#ffffff",
                backgroundColor: "transparent",

                "&:before": {
                  backgroundColor: "transparent",
                },
              },
            },
          },
          MuiAccordionSummary: {
            styleOverrides: {
              content: {
                alignItems: "center",
              },
            },
          },
          MuiAccordionDetails: {
            styleOverrides: {
              root: {
                padding: "8px 12px 12px",
              },
            },
          },
          MuiButton: {
            styleOverrides: {
              root: {
                textTransform: "capitalize",
                transition: "background-color 0.3s ease, color 0.3s ease",

                "&.FstoButtonPrimary-root": {
                  backgroundColor: COLORS.SECONDARY_COLOR,
                  color:
                    colorScheme === "dark" || (colorScheme === "system" && prefersDarkMode)
                      ? COLORS.WHITE
                      : COLORS.WHITE,
                  "&:hover": {
                    backgroundColor:
                      colorScheme === "dark" || (colorScheme === "system" && prefersDarkMode)
                        ? COLORS.ACCENT_HOVER_DARK_COLOR
                        : COLORS.ACCENT_HOVER_DARK_COLOR,
                  },
                  "&.Mui-disabled": {
                    backgroundColor:
                      colorScheme === "dark" || (colorScheme === "system" && prefersDarkMode)
                        ? COLORS.DISABLED_BG_DARK
                        : COLORS.DISABLED_BG_LIGHT,
                    color:
                      colorScheme === "dark" || (colorScheme === "system" && prefersDarkMode)
                        ? COLORS.DISABLED_C_DARK
                        : COLORS.DISABLED_C_LIGHT,

                    opacity: 0.6,
                  },
                },

                "&.FstoButtonSecondary-filled": {
                  backgroundColor: COLORS.ACCENT_COLOR,
                  color:
                    colorScheme === "dark" || (colorScheme === "system" && prefersDarkMode)
                      ? COLORS.WHITE
                      : COLORS.WHITE,
                  "&:hover": {
                    backgroundColor:
                      colorScheme === "dark" || (colorScheme === "system" && prefersDarkMode)
                        ? COLORS.ACCENT_HOVER_DARK_COLOR
                        : COLORS.ACCENT_HOVER_DARK_COLOR,
                  },
                  "&.Mui-disabled": {
                    backgroundColor:
                      colorScheme === "dark" || (colorScheme === "system" && prefersDarkMode)
                        ? COLORS.DISABLED_BG_DARK
                        : COLORS.DISABLED_BG_LIGHT,
                    color:
                      colorScheme === "dark" || (colorScheme === "system" && prefersDarkMode)
                        ? COLORS.DISABLED_C_DARK
                        : COLORS.DISABLED_C_LIGHT,
                    opacity: 0.6,
                  },
                },
                "&.FstoButtonSecondary-contained": {
                  backgroundColor:
                    colorScheme === "dark" || (colorScheme === "system" && prefersDarkMode)
                      ? "#2f2f35"
                      : "#f4f4f7",
                  color:
                    colorScheme === "dark" || (colorScheme === "system" && prefersDarkMode)
                      ? COLORS.WHITE
                      : "#111827",
                  "&:hover": {
                    backgroundColor:
                      colorScheme === "dark" || (colorScheme === "system" && prefersDarkMode)
                        ? COLORS.ACCENT_HOVER_DARK_COLOR
                        : COLORS.ACCENT_HOVER_DARK_COLOR,
                    color: COLORS.WHITE,
                  },
                  "&.Mui-disabled": {
                    backgroundColor:
                      colorScheme === "dark" || (colorScheme === "system" && prefersDarkMode)
                        ? COLORS.DISABLED_BG_DARK
                        : COLORS.DISABLED_BG_LIGHT,
                    color:
                      colorScheme === "dark" || (colorScheme === "system" && prefersDarkMode)
                        ? COLORS.DISABLED_C_DARK
                        : COLORS.DISABLED_C_LIGHT,
                    opacity: 0.6,
                  },
                },

                // Secondary Outlined Variant
                "&.FstoButtonSecondary-outlined": {
                  backgroundColor: "transparent",
                  border: `1px solid ${
                    colorScheme === "dark" || (colorScheme === "system" && prefersDarkMode)
                      ? COLORS.WHITE
                      : COLORS.ACCENT_COLOR
                  }`,
                  color:
                    colorScheme === "dark" || (colorScheme === "system" && prefersDarkMode)
                      ? COLORS.WHITE
                      : COLORS.PRIMARY_COLOR,
                  "&:hover": {
                    // Optionally, add a subtle background on hover
                    backgroundColor:
                      colorScheme === "dark" || (colorScheme === "system" && prefersDarkMode)
                        ? COLORS.ACCENT_HOVER_DARK_COLOR
                        : COLORS.ACCENT_HOVER_DARK_COLOR,
                    color:
                      colorScheme === "dark" || (colorScheme === "system" && prefersDarkMode)
                        ? COLORS.WHITE
                        : COLORS.WHITE,
                    border: `1px solid ${
                      colorScheme === "dark" || (colorScheme === "system" && prefersDarkMode)
                        ? COLORS.ACCENT_HOVER_DARK_COLOR
                        : COLORS.ACCENT_COLOR
                    }`,
                  },
                  "&.Mui-disabled": {
                    borderColor:
                      colorScheme === "dark" || (colorScheme === "system" && prefersDarkMode)
                        ? COLORS.DISABLED_BG_DARK
                        : COLORS.DISABLED_BG_LIGHT,
                    color:
                      colorScheme === "dark" || (colorScheme === "system" && prefersDarkMode)
                        ? COLORS.DISABLED_C_DARK
                        : COLORS.DISABLED_C_LIGHT,
                    opacity: 0.6,
                  },
                },
              },
            },
          },

          MuiTab: {
            styleOverrides: {
              root: {
                color:
                  colorScheme === "dark" || (colorScheme === "system" && prefersDarkMode)
                    ? COLORS.WHITE
                    : COLORS.PRIMARY_COLOR,
                "&.Mui-selected": {
                  color:
                    colorScheme === "dark" || (colorScheme === "system" && prefersDarkMode)
                      ? COLORS.SECONDARY_COLOR
                      : COLORS.ACCENT_COLOR,
                },
                "&:hover": {
                  color:
                    colorScheme === "dark" || (colorScheme === "system" && prefersDarkMode)
                      ? COLORS.ACCENT_HOVER_DARK_COLOR
                      : COLORS.ACCENT_COLOR,
                },
              },
            },
          },
          MuiCheckbox: {
            styleOverrides: {
              root: {
                color:
                  colorScheme === "dark" || (colorScheme === "system" && prefersDarkMode)
                    ? COLORS.WHITE
                    : COLORS.DISABLED_BG_DARK,
                "&.Mui-checked": {
                  color:
                    colorScheme === "dark" || (colorScheme === "system" && prefersDarkMode)
                      ? COLORS.WHITE
                      : COLORS.SECONDARY_COLOR,
                },
              },
            },
          },
          MuiOutlinedInput: {
            styleOverrides: {
              root: {
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor:
                    colorScheme === "dark" || (colorScheme === "system" && prefersDarkMode)
                      ? COLORS.DISABLED_BG_DARK
                      : COLORS.DISABLED_BG_DARK,
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor:
                    colorScheme === "dark" || (colorScheme === "system" && prefersDarkMode)
                      ? COLORS.ACCENT_COLOR
                      : COLORS.ACCENT_COLOR,
                },

                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor:
                    colorScheme === "dark" || (colorScheme === "system" && prefersDarkMode)
                      ? COLORS.ACCENT_COLOR
                      : COLORS.ACCENT_COLOR,
                },
              },
            },
          },

          MuiTableRow: {
            styleOverrides: {
              root: {
                "&.MuiTableRow-hover:hover": {
                  backgroundColor:
                    colorScheme === "dark" || (colorScheme === "system" && prefersDarkMode)
                      ? // ? "rgba(0, 0, 0, 0.1)"
                        COLORS.ACCENT_COLOR
                      : "#ece1f9",
                },
                "&.Mui-selected": {
                  backgroundColor:
                    colorScheme === "dark" || (colorScheme === "system" && prefersDarkMode)
                      ? COLORS.DISABLED_BG_DARK
                      : "#f2ecf9c7",
                },

                "&.FstoTableRowAccountTitle-root": {
                  borderBottom: `2px dotted ${
                    colorScheme === "dark" || (colorScheme === "system" && prefersDarkMode)
                      ? COLORS.WHITE
                      : COLORS.PRIMARY_COLOR
                  }`,
                  backgroundColor:
                    colorScheme === "dark" || (colorScheme === "system" && prefersDarkMode)
                      ? COLORS.DISABLED_BG_DARK
                      : "#f2ecf9c7",
                },
                "&.FstoTableRowCheque-root": {
                  borderBottom: `2px dotted ${
                    colorScheme === "dark" || (colorScheme === "system" && prefersDarkMode)
                      ? COLORS.WHITE
                      : COLORS.PRIMARY_COLOR
                  }`,
                  backgroundColor:
                    colorScheme === "dark" || (colorScheme === "system" && prefersDarkMode)
                      ? COLORS.DISABLED_BG_DARK
                      : "#f2ecf9c7",
                },
              },
            },
          },
          //BACKGROUND COLOR FOR DIALOGS
          MuiDialog: {
            styleOverrides: {
              paper: {
                ...(colorScheme === "dark" || (colorScheme === "system" && prefersDarkMode)
                  ? {
                      backgroundColor: "#000001",
                    }
                  : {}),
              },
            },
          },
          MuiSwitch: {
            styleOverrides: {
              root: {
                "& .MuiSwitch-thumb": {
                  backgroundColor:
                    colorScheme === "dark" || (colorScheme === "system" && prefersDarkMode)
                      ? COLORS.SECONDARY_COLOR
                      : COLORS.PRIMARY_COLOR,
                },
              },
            },
          },
          MuiStack: {
            styleOverrides: {
              root: {
                "&.FstoStackToolbar-root": {
                  paddingTop: 0,
                  backgroundColor:
                    colorScheme === "dark" || (colorScheme === "system" && prefersDarkMode)
                      ? "rgba(30, 30, 30, 0.7)" // Dark background
                      : "#ffffff", // Light background

                  boxShadow:
                    colorScheme === "dark" || (colorScheme === "system" && prefersDarkMode)
                      ? "0 2px 10px rgba(0, 0, 0, 0.5)" // Dark shadow
                      : "0 2px 10px rgba(0, 0, 0, 0.1)", // Light soft shadow

                  padding: "16px",
                  marginBottom: 1,
                  transition: "all 0.3s ease",
                },
              },
            },
          },
        },
        palette: {
          mode: colorScheme === "system" ? (prefersDarkMode ? "dark" : "light") : colorScheme,
          ...((colorScheme === "dark" || (colorScheme === "system" && prefersDarkMode)) && {
            background: {
              //  default: "#181818",
              default: "#181818",
            },
          }),
          pending: {
            main: "#F79647",
            light: "#F79647",
            dark: "#F79647",
            contrastText: "#F79647",
          },
          tag: {
            main: "#578FFE",
            light: "#578FFE",
            dark: "#578FFE",
            contrastText: "#578FFE",
          },
          voucher: {
            main: "#7030A0",
            light: "#7030A0",
            dark: "#7030A0",
            contrastText: "#7030A0",
          },
          approve: {
            main: "#77933C",
            light: "#77933C",
            dark: "#77933C",
            contrastText: "#77933C",
          },
          transmit: {
            main: "#31859D",
            light: "#31859D",
            dark: "#31859D",
            contrastText: "#31859D",
          },
          cheque: {
            main: "#F79647",
            light: "#F79647",
            dark: "#F79647",
            contrastText: "#F79647",
          },
          release: {
            main: "#F4D836",
            light: "#F4D836",
            dark: "#F4D836",
            contrastText: "#F4D836",
          },
          file: {
            main: "#B66837",
            light: "#B66837",
            dark: "#B66837",
            contrastText: "#B66837",
          },
        },
        zIndex: {
          modal: 1400,
          snackbar: 1300,
        },
      }),
    [colorScheme, prefersDarkMode]
  );

  return { theme };
};

export default useFistoTheme;
